import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useGroup } from '../context/GroupContext';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AddPledgeModal from '../components/modals/AddPledgeModal';
import PledgeTable from '../components/pledges/PledgeTable';
import ClaimSuccessModal from '../components/modals/ClaimSuccessModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faPlus,
  faSearch,
  faFilter,
  faEllipsisV,
  faEdit,
  faTrash,
  faCheckCircle,
  faCalendarAlt,
  faChevronDown
} from '@fortawesome/free-solid-svg-icons';
import '../styles/FinancialsPage.css';
import groupService from '../api/groupService';

const PledgesPage = () => {
  const { currentUser } = useAuth();
  const { adminGroup } = useGroup();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showClaimSuccessModal, setShowClaimSuccessModal] = useState(false);
  const [claimedPledge, setClaimedPledge] = useState(null);
  
  // Pledge types (filters)
  const [pledgeTypes, setPledgeTypes] = useState([]);
  const [selectedType, setSelectedType] = useState('all');
  
  // Pledges data
  const [pledges, setPledges] = useState([]);
  
  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [dateRange, setDateRange] = useState('all');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  
  // Modal states
  const [showCreatePledgeModal, setShowCreatePledgeModal] = useState(false);
  const [pledgeToEdit, setPledgeToEdit] = useState(null);
  const [showCreateTypeModal, setShowCreateTypeModal] = useState(false);
  const [showEditTypeModal, setShowEditTypeModal] = useState(false);
  const [showDeleteTypeModal, setShowDeleteTypeModal] = useState(false);
  const [selectedTypeForEdit, setSelectedTypeForEdit] = useState(null);
  const [editTypeName, setEditTypeName] = useState('');
  const [editTypeColor, setEditTypeColor] = useState('#3b82f6');

  // Check if coming from claim flow
  useEffect(() => {
    console.log('🎯 PledgesPage: Checking location.state:', location.state);
    if (location.state?.showClaimSuccess && location.state?.claimCode) {
      console.log('🎯 PledgesPage: Claim success detected, fetching pledge:', location.state.claimCode);
      fetchClaimedPledge(location.state.claimCode);
    }
  }, [location.state]);

  const fetchClaimedPledge = async (claimCode) => {
    try {
      console.log('🎯 PledgesPage: Fetching claimed pledge with code:', claimCode);
      // Get the staging pledge to show in modal
      const pledgeResponse = await fetch(`/api/pledges/claim/${claimCode}`);
      console.log('🎯 PledgesPage: Response status:', pledgeResponse.status);
      if (!pledgeResponse.ok) {
        console.log('❌ PledgesPage: Response not OK');
        return;
      }
      
      const pledgeData = await pledgeResponse.json();
      console.log('🎯 PledgesPage: Pledge data received:', pledgeData);
      
      // The API returns the pledge directly (not wrapped in success/pledge structure)
      if (!pledgeData || !pledgeData.id) {
        console.log('❌ PledgesPage: Invalid pledge data structure');
        return;
      }
      
      // Show the modal with pledge data
      console.log('✅ PledgesPage: Setting claimed pledge and showing modal');
      setClaimedPledge(pledgeData);
      setShowClaimSuccessModal(true);
      
      // Clear location state to prevent modal from showing again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    } catch (error) {
      console.error('❌ PledgesPage: Error fetching claimed pledge:', error);
    }
  };
  
  // Dropdown menu state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  
  // Pagination
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    hasMore: false
  });

  // Load pledge types
  useEffect(() => {
    if (!adminGroup?.id) return;
    
    const loadPledgeTypes = async () => {
      try {
        const response = await groupService.getPledgeTypes(adminGroup.id);
        const typesData = response?.data?.data || response?.data || [];
        setPledgeTypes(typesData);
      } catch (err) {
        console.error('Error loading pledge types:', err);
      }
    };
    
    loadPledgeTypes();
  }, [adminGroup?.id]);

  // Load pledges
  useEffect(() => {
    if (!adminGroup?.id) return;
    
    loadPledgesData(0, true);
  }, [adminGroup?.id]);

  // Reload pledges when page becomes visible (navigating back)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && adminGroup?.id) {
        console.log('Page became visible, reloading pledges');
        loadPledgesData();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [adminGroup?.id]);

  const loadPledgesData = async (offset = 0, showLoadingState = false) => {
    try {
      if (showLoadingState) {
        setLoading(true);
      }
      
      console.log('Loading pledges for group:', adminGroup?.id);
      
      const params = {
        limit: pagination.limit,
        offset
      };
      
      const response = await groupService.getPledges(adminGroup.id, params);
      console.log('Raw API response:', response);
      
      const pledgesData = response?.data?.data || response?.data || [];
      
      console.log('Loaded pledges:', pledgesData.length, 'pledges', pledgesData);
      
      if (offset === 0) {
        setPledges(pledgesData);
      } else {
        setPledges(prev => [...prev, ...pledgesData]);
      }
      
      setPagination(response?.pagination || pagination);
      setError(null);
    } catch (err) {
      console.error('Error loading pledges:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load pledges');
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
    }
  };

  const handleCreatePledge = () => {
    setShowCreatePledgeModal(true);
  };

  const handleEditPledge = (pledge) => {
    setPledgeToEdit(pledge);
    setShowCreatePledgeModal(true);
  };

  const handleDeletePledge = async (pledgeId) => {
    try {
      // Optimistically remove from UI
      setPledges(prevPledges => prevPledges.filter(p => p.id !== pledgeId));
      
      // Delete on backend
      await groupService.deletePledge(pledgeId);
      
      // Update pagination count
      setPagination(prev => ({ 
        ...prev, 
        total: Math.max(0, prev.total - 1) 
      }));
    } catch (err) {
      console.error('Error deleting pledge:', err);
      // Reload pledges to restore state on error
      loadPledgesData(pagination.offset);
      
      // Show error to user
      setError(err.message || 'Failed to delete pledge');
      setTimeout(() => setError(null), 5000);
    }
  };

  const handlePledgeCreated = async () => {
    // Reload pledge types in case a new one was created
    try {
      const response = await groupService.getPledgeTypes(adminGroup.id);
      const typesData = response?.data?.data || response?.data || [];
      setPledgeTypes(typesData);
    } catch (err) {
      console.error('Error reloading pledge types:', err);
    }
    
    // Reset pagination and reload pledges after creating a new one
    setPagination(prev => ({ ...prev, offset: 0 }));
    loadPledgesData(0, true);
    setShowCreatePledgeModal(false);
    setPledgeToEdit(null);
  };

  const handleCreateType = () => {
    setShowCreateTypeModal(true);
  };

  const handleDropdownToggle = (typeId, event) => {
    event.stopPropagation();
    setOpenDropdownId(openDropdownId === typeId ? null : typeId);
  };

  const handleEditPledgeType = (type) => {
    setSelectedTypeForEdit(type);
    setEditTypeName(type.name);
    setEditTypeColor(type.color);
    setShowEditTypeModal(true);
    setOpenDropdownId(null);
  };

  const handleDeletePledgeType = (type) => {
    setSelectedTypeForEdit(type);
    setShowDeleteTypeModal(true);
    setOpenDropdownId(null);
  };

  const handleConfirmEdit = async () => {
    try {
      // Optimistically update the UI
      const updatedTypes = pledgeTypes.map(type => 
        type.id === selectedTypeForEdit.id 
          ? { ...type, name: editTypeName, color: editTypeColor }
          : type
      );
      setPledgeTypes(updatedTypes);
      
      // Update on backend
      await groupService.updatePledgeType(adminGroup.id, selectedTypeForEdit.id, {
        name: editTypeName,
        color: editTypeColor
      });
      
      // Reload pledges to reflect updated type information
      await loadPledgesData(0, true);
      
      setShowEditTypeModal(false);
      setSelectedTypeForEdit(null);
    } catch (err) {
      console.error('Error updating pledge type:', err);
      // Revert optimistic update on error
      const response = await groupService.getPledgeTypes(adminGroup.id);
      const typesData = response?.data?.data || response?.data || [];
      setPledgeTypes(typesData);
      alert('Failed to update pledge type');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      // Optimistically remove from UI
      const filteredTypes = pledgeTypes.filter(type => type.id !== selectedTypeForEdit.id);
      setPledgeTypes(filteredTypes);
      
      // Reset selected type if it was the deleted one
      if (selectedType === selectedTypeForEdit.id) {
        setSelectedType('all');
      }
      
      // Delete on backend
      await groupService.deletePledgeType(adminGroup.id, selectedTypeForEdit.id);
      
      // Reload pledges to reflect removed type
      await loadPledgesData(0, true);
      
      setShowDeleteTypeModal(false);
      setSelectedTypeForEdit(null);
    } catch (err) {
      console.error('Error deleting pledge type:', err);
      // Revert optimistic update on error
      const response = await groupService.getPledgeTypes(adminGroup.id);
      const typesData = response?.data?.data || response?.data || [];
      setPledgeTypes(typesData);
      alert('Failed to delete pledge type');
    }
  };

  const handleLoadMore = () => {
    if (!loading && pagination.hasMore) {
      loadPledgesData(pagination.offset + pagination.limit);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId !== null && !event.target.closest('.product-type-chip-container')) {
        setOpenDropdownId(null);
      }
      if (showDateDropdown && !event.target.closest('.date-range-dropdown')) {
        setShowDateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdownId, showDateDropdown]);

  // Get date range filter
  const getDateRangeFilter = (range) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (range) {
      case 'today':
        return { start: today, end: new Date(today.getTime() + 24 * 60 * 60 * 1000) };
      case 'last7days':
        return { start: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000), end: now };
      case 'last30days':
        return { start: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000), end: now };
      case 'last6months':
        return { start: new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()), end: now };
      case 'thisyear':
        return { start: new Date(now.getFullYear(), 0, 1), end: now };
      default:
        return null;
    }
  };

  // Date range options
  const dateRangeOptions = [
    { value: 'all', label: 'All time' },
    { value: 'today', label: 'Today' },
    { value: 'last7days', label: 'Last 7 days' },
    { value: 'last30days', label: 'Last 30 days' },
    { value: 'last6months', label: 'Last 6 months' },
    { value: 'thisyear', label: 'This year' }
  ];

  // Filter pledges
  const filteredPledges = pledges.filter(pledge => {
    const matchesType = selectedType === 'all' || pledge.pledge_type_id === selectedType;
    const member = pledge.native_users;
    const memberName = member?.full_name || 
      (member?.first_name && member?.last_name ? `${member.first_name} ${member.last_name}` : '') ||
      member?.name || '';
    const memberEmail = member?.email || '';
    
    const matchesSearch = searchQuery === '' || 
      memberName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      memberEmail.toLowerCase().includes(searchQuery.toLowerCase()) ||
      pledge.description?.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Date range filter
    let matchesDate = true;
    if (dateRange !== 'all') {
      const dateFilter = getDateRangeFilter(dateRange);
      if (dateFilter) {
        const pledgeDate = new Date(pledge.created_at);
        matchesDate = pledgeDate >= dateFilter.start && pledgeDate <= dateFilter.end;
      }
    }
    
    return matchesType && matchesSearch && matchesDate;
  });

  // Sort pledges
  const sortedPledges = [...filteredPledges].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created_at) - new Date(a.created_at);
      case 'oldest':
        return new Date(a.created_at) - new Date(b.created_at);
      case 'amount_high':
        return b.amount - a.amount;
      case 'amount_low':
        return a.amount - b.amount;
      case 'due_date':
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return new Date(a.due_date) - new Date(b.due_date);
      default:
        return 0;
    }
  });

  // Calculate total paid pledges
  const totalPaidPledges = pledges
    .filter(pledge => pledge.payment_status === 'paid')
    .reduce((sum, pledge) => sum + (pledge.amount || 0), 0);

  // Render summary box for total paid pledges
  const renderSummaryBox = () => {
    return (
      <div className="summary-boxes">
        <div className="summary-box">
          <div className="summary-content">
            <div className="summary-left">
              <div className="summary-label">
                <FontAwesomeIcon icon={faCheckCircle} className="status-icon succeeded" />
                Total Paid Pledges
              </div>
              <div className="summary-value">
                ${totalPaidPledges.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render chip filters for pledge types
  const renderTypeFilters = () => (
    <div className="chip-filters-container">
      <div className="chip-filters">
        <button
          className={`chip-filter ${selectedType === 'all' ? 'active' : ''}`}
          onClick={() => setSelectedType('all')}
        >
          All
        </button>
        {pledgeTypes.map(type => (
          <div key={type.id} className="product-type-chip-container">
            <button
              className={`chip-filter ${selectedType === type.id ? 'active' : ''} chip-with-menu`}
              onClick={() => setSelectedType(type.id)}
            >
              <span>{type.name}</span>
              <div className="chip-menu-container">
                <div
                  className="chip-menu-button"
                  onClick={(e) => handleDropdownToggle(type.id, e)}
                  title="More options"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleDropdownToggle(type.id, e);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={faEllipsisV} />
                </div>
                {openDropdownId === type.id && (
                  <div className="chip-dropdown-menu">
                    <button
                      className="chip-dropdown-item"
                      onClick={() => handleEditPledgeType(type)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      Edit
                    </button>
                    <button
                      className="chip-dropdown-item delete"
                      onClick={() => handleDeletePledgeType(type)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      Delete
                    </button>
                  </div>
                )}
              </div>
            </button>
          </div>
        ))}
      </div>
      
      {/* Right side controls - Search Bar & Date Range */}
      <div className="right-controls">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400 text-sm" />
          </div>
          <input
            type="text"
            placeholder="Search pledges..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-72 pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
          />
        </div>
        
        {/* Date Range Filter */}
        <div className="date-range-picker">
          <div className="date-range-dropdown">
            <button
              className={`date-range-button ${dateRange !== 'all' ? 'active' : ''}`}
              onClick={() => setShowDateDropdown(!showDateDropdown)}
            >
              <FontAwesomeIcon icon={faCalendarAlt} />
              <span>{dateRangeOptions.find(opt => opt.value === dateRange)?.label || 'All time'}</span>
              <FontAwesomeIcon icon={faChevronDown} className="chevron" />
            </button>
            {showDateDropdown && (
              <div className="date-range-menu">
                {dateRangeOptions.map(option => (
                  <button
                    key={option.value}
                    className={`date-range-option ${dateRange === option.value ? 'active' : ''}`}
                    onClick={() => {
                      setDateRange(option.value);
                      setShowDateDropdown(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // Render pledges table
  const renderPledgesTable = () => {
    console.log('Rendering table with:', {
      totalPledges: pledges.length,
      filteredPledges: filteredPledges.length,
      sortedPledges: sortedPledges.length,
      loading
    });
    
    if (loading && pledges.length === 0) {
      return (
        <div className="loading-state">
          <p>Loading pledges...</p>
        </div>
      );
    }
    
    if (pledges.length === 0 && !loading) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FontAwesomeIcon icon={faFilter} />
          </div>
          <h3>No pledges yet</h3>
          <p>Create your first pledge to get started tracking commitments from your community members.</p>
          <button className="create-first-button" onClick={handleCreatePledge}>
            <FontAwesomeIcon icon={faPlus} /> Create First Pledge
          </button>
        </div>
      );
    }

    if (sortedPledges.length === 0 && pledges.length > 0) {
      return (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FontAwesomeIcon icon={faFilter} />
          </div>
          <h3>No matching pledges</h3>
          <p>Try adjusting your filters or search query.</p>
        </div>
      );
    }

    return (
      <PledgeTable
        pledges={sortedPledges}
        onLoadMore={handleLoadMore}
        hasMore={pagination.hasMore}
        loading={loading}
        onEdit={handleEditPledge}
        onDelete={handleDeletePledge}
      />
    );
  };

  return (
    <AdminLayout>
      <div className="financials-page">
        <div className="financials-container">
          {/* Page Header */}
          <div className="page-header">
            <div className="page-header-top">
              <h1>Pledges</h1>
              <button 
                className="add-donation-type-button"
                onClick={handleCreatePledge}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Pledge
              </button>
            </div>
          </div>

          {/* Error State */}
          {error && (
            <div className="error-state" style={{ 
              background: '#fef2f2', 
              border: '1px solid #fecaca', 
              borderRadius: '8px', 
              padding: '16px', 
              marginBottom: '24px',
              color: '#dc2626'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Summary Box */}
          {renderSummaryBox()}

          {/* Type Filters */}
          {renderTypeFilters()}

          {/* Pledges Table */}
          {renderPledgesTable()}
        </div>

        {/* Add Pledge Modal */}
        <AddPledgeModal
          isOpen={showCreatePledgeModal}
          onClose={() => {
            setShowCreatePledgeModal(false);
            setPledgeToEdit(null);
          }}
          onPledgeCreated={handlePledgeCreated}
          pledgeToEdit={pledgeToEdit}
        />

        {/* Edit Pledge Type Modal */}
        {showEditTypeModal && selectedTypeForEdit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  Edit Pledge Type
                </h2>
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-amber-600 mt-0.5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-amber-800">Warning</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Changing this pledge type name will update all existing pledges that use this type.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Type Name
                    </label>
                    <input
                      type="text"
                      value={editTypeName}
                      onChange={(e) => setEditTypeName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter type name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color
                    </label>
                    <input
                      type="color"
                      value={editTypeColor}
                      onChange={(e) => setEditTypeColor(e.target.value)}
                      className="w-full h-10 px-1 py-1 border border-gray-300 rounded-lg cursor-pointer"
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowEditTypeModal(false);
                      setSelectedTypeForEdit(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmEdit}
                    disabled={!editTypeName.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Pledge Type Modal */}
        {showDeleteTypeModal && selectedTypeForEdit && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>

                <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                  Delete Pledge Type
                </h2>

                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 text-center">
                    <strong>Warning:</strong> This action cannot be undone. Deleting this pledge type will remove it from the filters. Existing pledges will retain their type category for historical records.
                  </p>
                </div>

                <p className="text-center text-gray-700 mb-6">
                  Are you sure you want to delete <strong>"{selectedTypeForEdit.name}"</strong>?
                </p>

                <div className="flex justify-center space-x-3">
                  <button
                    onClick={() => {
                      setShowDeleteTypeModal(false);
                      setSelectedTypeForEdit(null);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDelete}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    Delete Type
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Claim Success Modal */}
        {(() => {
          console.log('🎯 Modal render check:', { showClaimSuccessModal, claimedPledge });
          return showClaimSuccessModal && claimedPledge;
        })() && (
          <ClaimSuccessModal
            show={showClaimSuccessModal}
            onClose={() => {
              setShowClaimSuccessModal(false);
              setClaimedPledge(null);
            }}
            requestData={claimedPledge}
            type="pledge"
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default PledgesPage;
